import { Alert, Box } from '@mui/material'
import { ReactNode } from 'react'
import {
  useLocation,
  useMatches,
  useNavigate,
  useParams,
} from 'react-router-dom'
import { ExtendedRouteObject } from 'src/routes/routes'
import LoadComponent from '~components/molecules/load-component/load-component'
import { useGetMe } from '~hooks/queries/me/use-get-me'
import { usePageShortcuts } from '~hooks/use-page-shortcuts'
import { useGetPools } from '~hooks/queries/pools/use-get-pools'
import Unauthorized from '~pages/auth/unauthorized-page'
import { removeLocalStorageItems } from '~utils/remove-local-storage-items'
import { useAuth0 } from '@auth0/auth0-react'

export function PageGuard({ children }: { children?: ReactNode }) {
  const { data, isLoading, isError } = useGetMe()
  const { logout } = useAuth0()
  const matches = useMatches()
  useReplacePoolParam()
  usePageShortcuts()

  if (isLoading) return <LoadComponent />
  if (isError || !data) {
    removeLocalStorageItems()
    logout({ logoutParams: { returnTo: window.location.origin } })
    return
  }

  if (isUserDisallowed(matches, data.scope)) {
    return <UnauthorizedBox />
  }

  if (!hasUserAccess(matches, data.scope)) {
    return <UnauthorizedBox />
  }

  return <>{children}</>
}

function isUserDisallowed(matches: any[], userScopes: string[]): boolean {
  return matches.some(
    // @ts-ignore
    (match) =>
      Array.isArray(match.handle?.disallowedScopes) &&
      match.handle.disallowedScopes.some((scope: string) =>
        userScopes.includes(scope),
      ),
  )
}

function hasUserAccess(matches: any[], userScopes: string[]): boolean {
  const restrictedRoutes = matches.filter(
    // @ts-ignore
    (match) =>
      Array.isArray(match.handle?.restrictedTo) &&
      match.handle.restrictedTo.length > 0,
  )
  if (restrictedRoutes.length === 0) return true
  return restrictedRoutes.some((route) =>
    matchScopes(route as ExtendedRouteObject, userScopes),
  )
}

function UnauthorizedBox() {
  return (
    <Box sx={{ height: '70vh', display: 'flex' }}>
      <Unauthorized />
    </Box>
  )
}

function matchScopes(routeMatch: ExtendedRouteObject, scopes: string[]) {
  if (!Array.isArray(routeMatch.handle?.restrictedTo)) {
    return true
  }

  return routeMatch.handle?.restrictedTo.some((r: string | RegExp) => {
    if (r instanceof RegExp) {
      return scopes.some((s) => r.test(s))
    }

    if (typeof r === 'string') {
      return scopes.includes(r)
    }

    return false
  })
}

function useReplacePoolParam() {
  const navigate = useNavigate()
  const params = useParams()
  const location = useLocation()
  const { data: pools } = useGetPools()

  const poolMatch = pools?.find((pool) => pool.id.toString() === params.pool)
  if (!poolMatch) return

  let pathPattern = location.pathname
  for (const key in params) {
    // @ts-ignore
    pathPattern = pathPattern.replace(params[key], `:${key}`)
  }

  // Replacing the parameter in the path pattern
  const newPath = pathPattern.replace(':pool', poolMatch.name.toLowerCase())
  navigate(newPath, { replace: true })
}
